<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <template v-if="!success || segment?.flow_id">
            <step-form-heading v-if="!props.segmentId" :icon="faScreenUsers">
                {{ props.templateView ? 'New Segment Template' : 'New Segment' }}

                <template v-if="props.templateView" v-slot:description>
                    Create a segment template that can be shared with groups.
                </template>
                <template v-else v-slot:description> Create automated groups of contacts. </template>
            </step-form-heading>

            <page-header v-if="props.segmentId" class="vx-mb-6 lg:vx-mb-12">
                <template v-slot:subtitle>
                    <a
                        :href="props.templateView ? '/contacts/segments/templates' : '/contacts/segments'"
                        class="!vx-no-underline !vx-text-sky-600 vx-text-base vx-font-normal"
                    >
                        <font-awesome-icon class="vx-mr-1" :icon="faArrowLeft"></font-awesome-icon>
                        All {{ props.templateView ? 'Segment Templates' : 'Segments' }}
                    </a>
                </template>

                {{ segment.name }}
            </page-header>

            <div
                :class="{
                    'vx-grid vx-grid-cols-12': props.segmentId,
                }"
            >
                <page-sidebar
                    v-if="props.segmentId"
                    :links="
                        [
                            {
                                href: `/contacts/segments/${props.templateView ? 'templates/' : ''}${props.segmentId}`,
                                label: 'Details',
                            },
                            {
                                href: `/contacts/segments/${props.templateView ? 'templates/' : ''}${
                                    props.segmentId
                                }#filters`,
                                label: 'Filters',
                            },
                            !props.templateView
                                ? { href: `/contacts/segments/${props.segmentId}/contacts`, label: 'Contacts' }
                                : null,
                        ].filter((link) => Boolean(link))
                    "
                ></page-sidebar>
                <vx-sticky-nav
                    :sections="
                        [
                            { id: 'name', label: 'Name' },
                            { id: 'category', label: 'Category' },
                            onCorporate() && !props.templateView ? { id: 'type', label: 'Type' } : null,
                            { id: 'audience', label: 'Audience' },
                            FEATURES.contact_groups ? { id: 'group', label: 'Groups' } : null,
                            { id: 'filters', label: 'Filters' },
                        ].filter((s) => !!s)
                    "
                    class="vx-col-span-12"
                    :class="{
                        'lg:vx-col-span-10': !!props.segmentId,
                    }"
                    :navClasses="`${success || props.segmentId ? 'vx-hidden' : ''}`"
                >
                    <div
                        v-if="segment?.flow_id"
                        class="vx-max-w-screen-sm vx-w-full"
                        :class="{
                            'vx-mx-auto': !props.segmentId,
                        }"
                    >
                        <flow-resource-state-alert
                            :resourceName="segment?.name"
                            :success="success"
                            :editing="!!segment?.id"
                            :flowId="segment?.flow_id"
                            :flowSection="segment?.flow_section"
                        >
                        </flow-resource-state-alert>
                    </div>

                    <div
                        v-if="!success || segment?.id"
                        class="vx-flex vx-flex-col vx-w-full vx-justify-center vx-gap-2 md:vx-gap-8"
                        :class="{
                            'vx-mx-auto': !props.segmentId,
                        }"
                    >
                        <div
                            id="name"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                        >
                            <segment-step-form-name
                                v-model="segment.name"
                                :error="errors.name || displayFirstError(validate.name)"
                            ></segment-step-form-name>
                        </div>

                        <resource-category
                            id="category"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                            v-model="segment.category"
                            :disabled="!!segment.flow_id"
                        >
                        </resource-category>

                        <div
                            v-if="onCorporate() && !props.templateView"
                            id="type"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                        >
                            <segment-step-form-type
                                v-model="segment.type"
                                :disabled="!!props.segmentId"
                            ></segment-step-form-type>
                        </div>
                        <div
                            id="audience"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                        >
                            <segment-step-form-audience
                                v-model="segment.audience_type"
                                :templateView="props.templateView"
                                :disabled="!!props.segmentId"
                            ></segment-step-form-audience>
                        </div>
                        <div
                            v-if="FEATURES.contact_groups && !onCorporate()"
                            id="group"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                        >
                            <segment-step-form-group-preferred
                                v-model="segment.preferred_group"
                            ></segment-step-form-group-preferred>
                        </div>
                        <div
                            v-if="FEATURES.contact_groups && !props.templateView && onCorporate()"
                            id="group"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                        >
                            <segment-step-form-group-collection
                                v-model="segment.group_collection_id"
                            ></segment-step-form-group-collection>
                        </div>
                        <div
                            v-if="FEATURES.contact_groups && props.templateView"
                            id="group"
                            :class="{
                                'vx-mx-auto vx-max-w-5xl vx-w-full': !props.segmentId,
                                'vx-w-full xl:vx-w-[62rem]': props.segmentId,
                            }"
                        >
                            <segment-step-form-group-access
                                :segmentId="props.segmentId"
                                v-model:groupFilters="segment.group_filters"
                                v-model:groupCollectionId="segment.group_collection_id"
                                v-model:preferredGroup="segment.preferred_group"
                                :error="displayFirstError(validate.group_filters)"
                            ></segment-step-form-group-access>
                        </div>
                        <div
                            id="filters"
                            :class="{
                                'vx-mx-auto vx-max-w-5xl vx-w-full': !props.segmentId,
                                'vx-w-full xl:vx-w-[62rem]': props.segmentId,
                            }"
                        >
                            <segment-step-form-filters
                                v-if="!props.segmentId || (props.segmentId && segment.id)"
                                v-model="segment.filter_groups"
                                :error="
                                    displayFirstError(validate.filter_groups) ||
                                    displayFirstError(validate.total_filters)
                                "
                            ></segment-step-form-filters>
                        </div>

                        <div
                            v-if="error"
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{ 'vx-mx-auto': !props.segmentId }"
                        >
                            <vx-alert color="danger" :closeable="false">
                                {{ error }}
                            </vx-alert>
                        </div>

                        <vx-sticky-footer
                            class="vx-max-w-screen-sm vx-w-full"
                            :class="{
                                'vx-mx-auto': !props.segmentId,
                            }"
                        >
                            <vx-button
                                :href="props.templateView ? '/contacts/segments/templates' : '/contacts/segments'"
                                data-test="cancel-button"
                                class="vx-w-20"
                                size="lg"
                                color="muted"
                            >
                                Cancel
                            </vx-button>
                            <vx-button
                                @click="submit()"
                                data-test="submit-btn"
                                class="vx-grow"
                                size="lg"
                                color="primary"
                                :loading="submiting"
                            >
                                {{ props.segmentId ? 'Update' : 'Create' }}
                                {{ props.templateView ? 'Template' : 'Segment' }}
                            </vx-button>
                        </vx-sticky-footer>
                    </div>
                </vx-sticky-nav>
            </div>
        </template>

        <template v-else>
            <success-box>
                Your {{ props.templateView ? 'template' : 'segment' }} has been
                {{ props.segmentId ? 'updated' : 'created' }}!

                <template v-slot:action>
                    <vx-button
                        size="lg"
                        :href="props.templateView ? '/contacts/segments/templates' : '/contacts/segments'"
                        color="secondary"
                    >
                        View All {{ props.templateView ? 'Templates' : 'Segments' }}
                        <font-awesome-icon :icon="faArrowRight"></font-awesome-icon>
                    </vx-button>
                </template>
            </success-box>
        </template>
    </form-container>
</template>

<script setup>
import { VxStickyNav, VxStickyFooter, VxButton, VxAlert } from '@voxie/frontend-components';
import StepFormHeading from '../../../general/StepFormHeading.vue';
import SuccessBox from '../../../general/SuccessBox.vue';
import SegmentStepFormName from './SegmentStepFormName.vue';
import SegmentStepFormType from './SegmentStepFormType.vue';
import SegmentStepFormAudience from './SegmentStepFormAudience.vue';
import SegmentStepFormGroupPreferred from './SegmentStepFormGroupPreferred.vue';
import SegmentStepFormGroupAccess from './SegmentStepFormGroupAccess.vue';
import SegmentStepFormGroupCollection from './SegmentStepFormGroupCollection.vue';
import SegmentStepFormFilters from './segment-step-form-filters/SegmentStepFormFilters.vue';
import PageHeader from '../../../general/PageHeader.vue';
import PageLoading from '../../../general/PageLoading.vue';
import PageSidebar from '../../../general/PageSidebar.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft, faArrowRight, faScreenUsers } from '@fortawesome/pro-solid-svg-icons';
import { ref, inject, onMounted, computed } from 'vue';
import segmentsClient from '~/services/segmentsClient';
import { SegmentType } from '../../../../constants/segment';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '../../../../constants/campaign';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_UNPROCESSABLE_ENTITY } from '../../../../support/HttpStatuses';
import { useToasted } from '../../../../composables/useToasted';
import { useUrlSearchParams } from '@vueuse/core';
import FlowResourceStateAlert from '~/components/automation/flows/components/FlowResourceStateAlert.vue';
import flowsClient from '~/services/flowsClient';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import useVuelidate from '@vuelidate/core';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { displayFirstError } from '~/utils/validation';
import { helpers, maxLength, maxValue, required } from '@vuelidate/validators';

const FEATURES = inject('FEATURES');

const toasted = useToasted();

const props = defineProps({
    segmentId: {
        type: Number,
        required: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});

const teamId = Spark.state.currentTeam.id;
const { onCorporate, getCurrentGroupId } = useCurrentGroup();

const loading = ref(false);
const segment = ref({
    name: '',
    type: SegmentType.AUTO_REFRESH,
    audience_type: FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING,
    preferred_group: false,
    group_collection_id: null,
    filter_groups: [],
    group_filters: [],
    category: '',
});

const errors = ref({});
const error = ref('');
const submiting = ref(false);

const success = ref(false);

const validate = useVuelidate(
    {
        name: {
            required,
            maxLength: maxLength(96),
            regex: helpers.withMessage(
                'Segment name is invalid',
                helpers.regex(/^[a-zA-Z0-9][a-zA-Z0-9:/\\@#$%&*()\-=_+[\],?. ]*$/)
            ),
        },
        audience_type: {
            required,
        },
        type: {
            required,
        },
        filter_groups: {
            maxLength: helpers.withMessage('Segment should not contain more than 10 Filter Groups.', maxLength(10)),
        },
        total_filters: {
            maxValue: helpers.withMessage('Segment should not contain more than 100 Filters.', maxValue(100)),
        },
        group_filters: {
            maxLength: helpers.withMessage('Segment should not contain more than 10 Group Filters.', maxLength(10)),
        },
    },
    computed(() => ({ ...segment.value, total_filters: totalFiltersCount.value }))
);

const totalFiltersCount = computed(() =>
    segment.value.filter_groups.reduce((total, group) => {
        return total + group.filters.length;
    }, 0)
);

const submit = async () => {
    try {
        const isValid = await validate.value.$validate();
        if (!isValid) {
            toasted.error('Please check form data.');
            return;
        }

        submiting.value = true;

        if (onCorporate()) {
            await (props.segmentId
                ? segmentsClient.updateSegment(teamId, props.segmentId, segment.value)
                : segmentsClient.createSegment(teamId, segment.value));
        } else {
            const data = {
                name: segment.value.name,
                audience_type: segment.value.audience_type,
                category: segment.value.category,
                filter_groups: segment.value.filter_groups,
                preferred_group: segment.value.preferred_group,
            };
            await (props.segmentId
                ? segmentsClient.updateGroupSegment(teamId, getCurrentGroupId(), props.segmentId, data)
                : segmentsClient.createGroupSegment(teamId, getCurrentGroupId(), data));
        }

        success.value = true;
        validate.value.$reset();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
        console.error(e);

        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        } else if (e.response.status === HTTP_UNPROCESSABLE_ENTITY) {
            errors.value = e.response.data.errors;
            error.value = e.response?.data?.message || 'Something went wrong.';
        } else {
            error.value = e.response?.data?.message || 'Something went wrong.';
        }

        toasted.error('Please check form data.');
    } finally {
        submiting.value = false;
    }
};

const queryParams = useUrlSearchParams();

const duplicate = async () => {
    const response = await segmentsClient.getSegment(teamId, queryParams.duplicateFromId);

    let flowSection;
    if (response.data.flow_id) {
        try {
            const {
                data: {
                    config: { sections },
                },
            } = await flowsClient.getConfig(teamId, response.data.flow_id);
            flowSection = sections.findIndex((section) =>
                section.resources.find(
                    (resource) =>
                        resource.type === 'segment' && Number(resource.id) === Number(queryParams.duplicateFromId)
                )
            );
        } catch (e) {
            console.error(e);
        }
    }

    segment.value = {
        ...response.data,
        id: undefined,
        name: '',
        flow_section: flowSection === -1 ? 0 : flowSection,
    };
};

onMounted(async () => {
    loading.value = true;
    try {
        if (props.segmentId) {
            segment.value = (await segmentsClient.getSegment(teamId, props.segmentId)).data;
        }

        if (queryParams.duplicateFromId) {
            await duplicate();
        }

        if (!segment.value.flow_id && queryParams.flow_id) {
            segment.value.flow_id = queryParams.flow_id;
            segment.value.flow_section = queryParams.flow_section || 0;
        }

        segment.value.is_template = props.templateView;
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
    }
});
</script>
